import { useId } from "react";

type Props = Omit<React.ComponentPropsWithRef<"input">, "id" | "className"> & {
  readonly label: string;
  /** One line under the field: what it is for, never a repeat of the label. */
  readonly hint?: string | undefined;
};

/**
 * The one text input of the product. Its label is always visible and bound to
 * it, its hint is announced with it, and focus is the shared ring — so no form
 * re-types forty classes and drifts from the others.
 */
export function TextField({ label, hint, ...input }: Props): React.JSX.Element {
  const id = useId();
  const hintId = useId();
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-sm text-text-secondary">
        {label}
      </label>
      <input
        {...input}
        id={id}
        aria-describedby={hint === undefined ? input["aria-describedby"] : hintId}
        className="mt-1 w-full rounded-md border border-border-subtle bg-background-base px-3 py-2 text-text-primary placeholder:text-text-muted placeholder:opacity-60 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:opacity-60"
      />
      {hint !== undefined && (
        <p id={hintId} className="mt-1 text-helper-sm text-text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}
